import { AsyncPipe } from '@angular/common'
import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { TranslatePipe } from '@ngx-translate/core'
import { filter, map, of, shareReplay } from 'rxjs'
import { AnalyticsDashboardComponent } from '../analytics-dashboard/analytics-dashboard.component'
import { ClientsService } from '../clients.service'
import { MetricsService } from '../metrics.service'
import { scopedId } from '../multi-environment/environment-merge'
import { SELF_ENVIRONMENT } from '../multi-environment/support-environment'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'

@Component({
  selector: 'app-client-dashboard',
  standalone: true,
  imports: [ToolbarComponent, TranslatePipe, AnalyticsDashboardComponent, SpinnerComponent, AsyncPipe],
  templateUrl: './client-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './client-dashboard.component.scss',
})
export class ClientDashboardComponent {
  clientsService = inject(ClientsService)
  usersService = inject(UsersService)
  metricsService = inject(MetricsService)

  currentUser$ = this.usersService.currentUser$
  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  /**
   * Null while it is still loading as well as for a client with no contract recorded, which is the
   * same thing as far as the dashboard is concerned: neither offers the subscription period.
   */
  subscriptionUsage$ = this.metricsService.getSubscriptionUsage().pipe(
    map((result) => result.data ?? null),
    shareReplay(1)
  )

  /**
   * Every team of the client, for the figures about its people. Scoped to the environment serving this
   * page, which is the only one a customer's dashboard ever reads: the analytics screens are shared
   * with support's cross-environment view, where the plain row id would not say which environment's
   * team it means.
   */
  teamIds$ = this.client$.pipe(
    filter((result) => result.isSuccess),
    map((result) => (result.data?.teams ?? []).map((team) => scopedId(SELF_ENVIRONMENT, team.id))),
    shareReplay(1)
  )

  // Empty, which the admin routes read as the caller's own client: every meeting belongs to one client.
  clientIds$ = of<string[]>([])
}
